import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRefreshTranslationsMutation, useTranslationsStatusQuery } from '@proaxiv/server-api'
import { ConfirmationModal } from '../components/ConfirmationModal'
import { Button } from '../components/Button'
import { formatLocalDateTime } from '../utils/datetime'

/** Shape of the (untyped, `object`-typed by the generator) refresh-translations response body —
 *  see engine `AdminController.RefreshTranslationsResponse`. */
interface RefreshTranslationsResult {
  success: boolean
  files: number
  version: string
  errors: string[]
  loadedAt?: string | null
}

/** Shape of `AdminController.TranslationsStatusResponse`. */
interface TranslationsStatus {
  files: number
  version: string
  loadedAt?: string | null
}

const WarningIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 9v4M12 17h.01" />
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
  </svg>
)

/**
 * Admin-only settings page. Currently hosts a single "Translations" section that lets an admin
 * force the engine to re-fetch the live translation bundles (from Weblate in staging/prod, or
 * from `i18n/` in dev — see `I18nProvider`) and atomically swap them in, without a deploy.
 *
 * Also shows when the in-memory snapshot was last loaded successfully (boot fetch or a prior
 * refresh on this engine process).
 *
 * Server-side enforcement already exists (`AdminController` gates on the Drools
 * `REFRESH_TRANSLATIONS` action); the `AdminGuard` route wrapper here is UX-only.
 */
export function SettingsPage() {
  const { t, i18n } = useTranslation()
  const { data: statusData } = useTranslationsStatusQuery()
  const [refreshTranslations, { isLoading }] = useRefreshTranslationsMutation()
  const [confirming, setConfirming] = useState(false)
  const [outcome, setOutcome] = useState<RefreshTranslationsResult | null>(null)
  const [loadedAtOverride, setLoadedAtOverride] = useState<string | null | undefined>(undefined)

  const status = statusData as TranslationsStatus | undefined
  const loadedAt = loadedAtOverride !== undefined ? loadedAtOverride : (status?.loadedAt ?? null)

  async function handleConfirmRefresh() {
    setConfirming(false)
    try {
      const result = (await refreshTranslations().unwrap()) as unknown as RefreshTranslationsResult
      setOutcome(result)
      if (result.success) {
        setLoadedAtOverride(result.loadedAt ?? null)
      }
    } catch {
      setOutcome({ success: false, files: 0, version: '', errors: [] })
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-pax-black text-2xl font-bold tracking-tight md:text-3xl">
          {t('nav_translations')}
        </h1>
      </div>

      <section className="border-pax-purple/20 space-y-4 rounded-2xl border bg-white p-5">
        <h2 className="text-pax-black text-lg font-semibold">{t('nav_translations')}</h2>

        <p className="text-pax-gray text-sm">
          {loadedAt
            ? t('settings_translations_last_refresh', {
                datetime: formatLocalDateTime(loadedAt, i18n.language),
              })
            : t('settings_translations_last_refresh_never')}
        </p>

        <div className="border-pax-orange-warm/30 bg-pax-orange-warm/10 flex items-start gap-3 rounded-xl border p-3">
          <span className="text-pax-orange-warm mt-0.5 shrink-0">
            <WarningIcon />
          </span>
          <p className="text-pax-black text-sm">{t('settings_translations_warning')}</p>
        </div>

        <Button variant="primary" disabled={isLoading} onClick={() => setConfirming(true)}>
          {t('settings_refresh_btn')}
        </Button>

        {outcome && (
          <p
            role="status"
            className={[
              'rounded-xl px-3 py-2 text-sm',
              outcome.success ? 'bg-pax-green/10 text-pax-green' : 'bg-pax-red/10 text-pax-red',
            ].join(' ')}
          >
            {outcome.success
              ? t('settings_refresh_success', { files: outcome.files, version: outcome.version })
              : t('settings_refresh_error')}
          </p>
        )}
      </section>

      {confirming && (
        <ConfirmationModal
          title={t('settings_refresh_confirm_title')}
          description={t('settings_refresh_confirm_text')}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmRefresh()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  )
}
